import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEye, FiCalendar, FiTag, FiDownload } from 'react-icons/fi';
import { postsAPI } from '../utils/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

const DetallePost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contentRef = useRef();

  useEffect(() => {
    cargarPost();
  }, [slug]);

  const cargarPost = async () => {
    try {
      setLoading(true);
      const response = await postsAPI.getPorSlug(slug);
      setPost(response.data.data);
      
      // Incrementar visualizaciones (usar slug, no id)
      await postsAPI.visualizar(slug);
    } catch (error) {
      console.error('Error al cargar post:', error);
      setError('No se pudo cargar el estudio');
    } finally {
      setLoading(false);
    }
  };

  const exportarPDF = async () => {
    if (!contentRef.current) return;

    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`${post.slug}.pdf`);
    } catch (error) {
      console.error('Error al exportar PDF:', error);
    }
  };

  const exportarWord = async () => {
    try {
      // Crear un div temporal para parsear el HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = post.contenido;
      
      const children = [];

      // Título
      children.push(
        new Paragraph({
          text: post.titulo,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 }
        })
      );

      // Descripción
      if (post.descripcion) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: post.descripcion, italics: true })],
            spacing: { after: 200 }
          })
        );
      }

      // Contenido (simplificado - se pierde algo de formato HTML)
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      const paragraphs = textContent.split('\n').filter(p => p.trim());
      
      paragraphs.forEach(text => {
        if (text.trim()) {
          children.push(
            new Paragraph({
              children: [new TextRun(text)],
              spacing: { after: 100 }
            })
          );
        }
      });

      const doc = new Document({
        sections: [{
          properties: {},
          children: children
        }]
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${post.slug}.docx`);
    } catch (error) {
      console.error('Error al exportar Word:', error);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-red-600 text-lg mb-4">{error || 'Post no encontrado'}</p>
        <Link to="/estudios" className="text-indigo-600 hover:underline">
          ← Volver a Estudios
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/estudios')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <FiArrowLeft className="mr-2" />
            Volver a Estudios
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Imagen destacada */}
          {post.imagen && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.imagen}
                alt={post.titulo}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8" ref={contentRef}>
            {/* Título */}
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {post.titulo}
            </h1>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b">
              <span className="flex items-center">
                <FiCalendar className="mr-1" />
                {formatDate(post.fechaPublicacion || post.createdAt)}
              </span>
              <span className="flex items-center">
                <FiEye className="mr-1" />
                {post.visualizaciones || 0} vistas
              </span>
              {post.autor && (
                <span>Por {post.autor.nombre}</span>
              )}
            </div>

            {/* Categorías */}
            {post.categorias && post.categorias.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.categorias.map((categoria) => (
                  <span
                    key={categoria.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: `${categoria.color}20`,
                      color: categoria.color
                    }}
                  >
                    <FiTag className="mr-1" size={14} />
                    {categoria.nombre}
                  </span>
                ))}
              </div>
            )}

            {/* Descripción */}
            {post.descripcion && (
              <div className="text-lg text-gray-700 mb-6 italic border-l-4 border-indigo-500 pl-4 py-2 bg-indigo-50">
                {post.descripcion}
              </div>
            )}

            {/* Contenido HTML */}
            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.contenido }}
            />

            {/* Palabras clave */}
            {post.palabrasClave && (
              <div className="mt-8 pt-6 border-t">
                <p className="text-sm text-gray-600">
                  <strong>Palabras clave:</strong> {post.palabrasClave}
                </p>
              </div>
            )}
          </div>

          {/* Botones de exportación */}
          <div className="px-8 pb-8 flex gap-4">
            <button
              onClick={exportarPDF}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              <FiDownload className="mr-2" />
              Exportar PDF
            </button>
            <button
              onClick={exportarWord}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <FiDownload className="mr-2" />
              Exportar Word
            </button>
          </div>
        </article>
      </div>
    </div>
  );
};

export default DetallePost;

